const restaurantOrdersTemplate = {
  channel: "voice",
  business_summary:
    "A restaurant taking takeout / delivery order calls and texting confirmations.",
  industry_tag: "Restaurant",
  goal: {
    primary: "Take a food order",
    summary:
      "Greet the caller, take their order, capture pickup / delivery details, and text a confirmation.",
  },
  fields: [
    {
      key: "restaurant_name",
      label: "Restaurant name",
      type: "text",
      value: null,
      source: "missing",
      required: true,
      placeholder: "e.g. Bella's Pizza",
      can_skip: true,
      skip_fallback: "our restaurant",
    },
    {
      key: "menu_summary",
      label: "Menu summary",
      type: "textarea",
      value: null,
      source: "missing",
      placeholder: "Pasta, wood-fired pizza, salads, desserts. Full menu via Knowledge below.",
      help_text: "Upload your full menu as a PDF on the next step for accuracy.",
    },
    {
      key: "service_modes",
      label: "We offer",
      type: "multiselect",
      options: ["Pickup", "Delivery", "Dine-in"],
      value: ["Pickup", "Delivery"],
      source: "default",
    },
    {
      key: "delivery_zip_codes",
      label: "Delivery ZIP codes",
      type: "text",
      value: null,
      source: "missing",
      placeholder: "78701, 78702, 78703",
    },
    {
      key: "payment_method",
      label: "Payment",
      type: "select",
      options: ["Paid on pickup / delivery", "Paid by card on call"],
      value: "Paid on pickup / delivery",
      source: "default",
    },
  ],
  recommended_tools: [
    {
      tool_key: "twilio_sms",
      reason: "Text the order confirmation + ETA to the caller.",
    },
  ],
  recommended_contact_syncs: [
    {
      tool_key: "crm_picker",
      fields_to_be_synced: {
        first_name: "first name",
        phone: "phone number",
        address: "delivery address (if applicable)",
        order_summary: "summary of items ordered",
        total: "estimated total",
      },
      reason: "Save the order against the caller for repeat-customer tracking.",
    },
  ],
  recommended_conversation_syncs: [],
  recommended_outcomes: [
    {
      key: "sms_confirmation",
      label: "Text an order confirmation",
      default_on: true,
      mechanism: "tool",
      tool_key: "twilio_sms",
    },
    {
      key: "save_order",
      label: "Save the order as a contact note",
      default_on: false,
      mechanism: "contact_sync",
      tool_key: "crm_picker",
    },
    {
      key: "summary_email",
      label: "Email the kitchen a summary of every order",
      default_on: true,
      mechanism: "summary_email",
    },
  ],
  voice_defaults: {
    first_sentence:
      "Thanks for calling {{restaurant_name}} — are you placing an order?",
    speaks_first: "bot",
    detect_voicemail: "hangup",
    voicemail_message:
      "Sorry we couldn't pick up. Please leave your name and number and we'll call you right back.",
    max_call_duration: 480,
    use_turn_detector: true,
    listen_silence: 20,
    listen_silence_message: "Are you still there? Just take your time.",
  },
  behavior_defaults: {
    llm_temperature: 0.3,
    has_human_agent: false,
    use_tools: true,
    summarize: null,
  },
  prompt_skeleton: `You are the order-taker for {{restaurant_name}}.

Operating context:
- Menu: {{menu_summary}}
- Service modes: {{service_modes|comma-list}}
- Delivery ZIPs: {{delivery_zip_codes}}
- Payment: {{payment_method}}

Conversation flow:
1. Opening — greet warmly: "Thanks for calling {{restaurant_name}} — are you placing an order?"
2. Pickup vs delivery — confirm which. If delivery, ask for the delivery address NOW (before taking items, so we can flag out-of-zone early). If the ZIP isn't in {{delivery_zip_codes}}, politely explain and offer pickup instead.
3. Take the order — item by item:
   - After EACH item: repeat it back exactly with quantity and any modifiers ("So that's one large pepperoni pizza, extra cheese — correct?").
   - Ask about common pairings (drinks, sides, desserts) ONCE near the end if they haven't mentioned them. Don't upsell aggressively.
   - If they request something not on the menu, say so honestly and offer the closest match.
4. Modifications + allergies — after the items: "Any allergies or special instructions on any of these?" Don't skip this — capture clearly for the kitchen.
5. Capture identity — name + phone (mirror digits back).
6. Quote ETA — pickup: 15-30 min. Delivery: 30-45 min. If it's obviously a peak time (Fri/Sat dinner), pad by 15 min and tell them honestly.
7. Read back — full order one more time, item by item with totals if you can compute them, the ETA, the name on the order, the address (if delivery). Get an explicit "yes that's right."
8. Send confirmation — call the twilio_sms tool to text them the order summary + ETA. If it errors, just continue verbally — don't tell them the SMS failed.
9. Close — "Thanks {{first name}}, we'll see you in about [ETA]." End politely.

Turn-taking rules:
- Voice: 1-2 sentence turns. Take ONE item at a time. Never let multiple items accumulate without confirming.
- Mirror back: phone numbers digit-by-digit; addresses as full read-back; any unusual items by full name.

Tone:
Warm, efficient. The caller is hungry — they appreciate a quick, accurate order. Match a chatty caller with light banter; match a brisk caller with crisp efficiency. Smile through your voice.

Boundaries — never:
- Confirm an item that isn't on the menu.
- Promise an ETA outside the bands above without checking — better to over-quote and over-deliver.
- Take a credit card by phone unless {{payment_method}} explicitly says so.
- Commit to delivery outside our zone.

Fallbacks:
If asked about ingredients, allergens, or sourcing you don't know: say "Let me grab the kitchen — they'll have the full ingredients list" and offer to take a callback request.

Escalation:
- Order > 6 items or > $200: pause and confirm with the kitchen before committing (offer "let me confirm the kitchen can handle this in our normal time — can I call you right back?").
- Caller frustrated about a previous order, allergic reaction, etc.: capture the issue, name, phone, and tell them the manager will call within the hour.`,
  confidence: 1.0,
  missing_critical_fields: ["restaurant_name", "menu_summary"],
};

export default restaurantOrdersTemplate;
